// browser-socket.ts
import {
	Events,
	IWebSocketCommon,
	IWebsocketsClient,
	Logger,
} from '@elyx-code/common-ts-utils';
import { parseRTCMessage } from './utils';
import { ILiveMessageDTO } from '@elyx-code/definitions';

type BrowserSocketOptions = {
	socketUrl: string;
	maxQueue?: number; // default 1000
	connectTimeoutMs?: number; // default 10_000
	heartbeatMs?: number; // default 30_000
	idleRestartMs?: number; // default 120_000
	backoffInitialMs?: number; // default 500
	backoffMaxMs?: number; // default 15_000
};

export class Socket extends Events implements IWebsocketsClient {
	public connection!: IWebSocketCommon;

	private url: string;
	private queue: ILiveMessageDTO[] = [];

	private reconnectAttempts = 0;
	private manualClose = false;
	private connectTimer?: number;
	private heartbeatTimer?: number;
	private idleTimer?: number;
	private lastSeenAt = Date.now();

	private readonly MAX_QUEUE: number;
	private readonly CONNECT_TIMEOUT_MS: number;
	private readonly HEARTBEAT_MS: number;
	private readonly IDLE_RESTART_MS: number;
	private readonly BACKOFF_INITIAL_MS: number;
	private readonly BACKOFF_MAX_MS: number;

	constructor(options: BrowserSocketOptions) {
		super();

		this.url = options.socketUrl;
		this.MAX_QUEUE = options.maxQueue ?? 1000;
		this.CONNECT_TIMEOUT_MS = options.connectTimeoutMs ?? 10_000;
		this.HEARTBEAT_MS = options.heartbeatMs ?? 30_000;
		this.IDLE_RESTART_MS = options.idleRestartMs ?? 120_000;
		this.BACKOFF_INITIAL_MS = options.backoffInitialMs ?? 500;
		this.BACKOFF_MAX_MS = options.backoffMaxMs ?? 15_000;

		this.open();
	}

	// ---- lifecycle -----------------------------------------------------------

	private open() {
		const ws = new WebSocket(this.url);

		// patch close() so external callers (socket.connection.close()) stop reconnection
		const originalClose = ws.close.bind(ws);
		ws.close = (code?: number, reason?: string) => {
			// this.manualClose = true;
			return originalClose(code, reason);
		};

		this.connection = ws as any as IWebSocketCommon;

		// connect-timeout guard
		this.clearTimer(this.connectTimer);
		this.connectTimer = window.setTimeout(() => {
			if (
				ws.readyState !== WebSocket.OPEN &&
				ws.readyState !== WebSocket.CLOSED
			) {
				try {
					ws.close(4000, 'connect-timeout');
				} catch {}
			}
		}, this.CONNECT_TIMEOUT_MS);

		// listeners — attach first to avoid missing early events
		ws.addEventListener('open', this.onOpen as any);
		ws.addEventListener('message', this.onMessageEvent as any);
		ws.addEventListener('error', this.onError as any);
		ws.addEventListener('close', this.onClose as any);
	}

	private onOpen = () => {
		this.reconnectAttempts = 0;
		this.lastSeenAt = Date.now();

		// flush queue
		const toSend = this.queue.splice(0, this.queue.length);
		for (const m of toSend) this._send(m);

		this.startHeartbeat();
		this.startIdleWatch();

		this.emit('open', null);
	};

	private onMessageEvent = async (e: MessageEvent) => {
		this.lastSeenAt = Date.now();
		const data = e.data;

		const messageStr =
			typeof data === 'string'
				? data
				: data instanceof ArrayBuffer
				? new TextDecoder().decode(data)
				: data instanceof Blob
				? await data.text()
				: '';

		const parsedMessage: ILiveMessageDTO | string = parseRTCMessage(messageStr);
		this.emit('message', parsedMessage);
	};

	private onError = (event: Event) => {
		this.emit('error', event);
	};

	private onClose = (evt: CloseEvent) => {
		this.stopHeartbeat();
		this.stopIdleWatch();
		this.clearTimer(this.connectTimer);

		const code = evt.code;
		const reason = (evt.reason ?? '').toString();

		this.emit('closed', { code, reason });

		if (this.manualClose) {
			Logger.log('WebSocket closed manually, not reconnecting');
			return
		};

		this.scheduleReconnect(`code=${code} reason=${reason}`);
	};

	// ---- resilience helpers --------------------------------------------------

	private scheduleReconnect(debugReason: string) {
		const base = Math.min(
			this.BACKOFF_INITIAL_MS * 2 ** this.reconnectAttempts,
			this.BACKOFF_MAX_MS
		);
		const jitter = Math.floor(Math.random() * 800);
		const delay = base + jitter;
		this.reconnectAttempts++;

		Logger.log(`WebSocket disconnected, scheduling reconnect in: ${delay}ms ${debugReason}`);

		window.setTimeout(() => this.open(), delay);

		this.emit('reconnecting', {
			attempt: this.reconnectAttempts,
			delay,
			reason: debugReason,
		});
	}

	private startHeartbeat() {
		this.stopHeartbeat();
		this.heartbeatTimer = window.setInterval(() => {
			// App-level ping; safe for API Gateway and browsers
			try {
				this.connection?.send?.(
					JSON.stringify({ type: 'ping', t: Date.now() })
				);
			} catch {}
		}, this.HEARTBEAT_MS);
	}

	private stopHeartbeat() {
		this.clearTimer(this.heartbeatTimer);
		this.heartbeatTimer = undefined;
	}

	private startIdleWatch() {
		this.stopIdleWatch();
		this.idleTimer = window.setInterval(() => {
			const idleFor = Date.now() - this.lastSeenAt;
			if (idleFor >= this.IDLE_RESTART_MS) {
				try {
					this.connection?.close?.(4001, 'idle-restart');
				} catch {}
			}
		}, Math.min(5_000, this.IDLE_RESTART_MS / 2));
	}

	private stopIdleWatch() {
		this.clearTimer(this.idleTimer);
		this.idleTimer = undefined;
	}

	private clearTimer(t?: number) {
		if (t) window.clearTimeout(t);
	}

	// ---- public send API -----------------------------------------------------

	private _send(outgoingMessage: ILiveMessageDTO) {
		if (!this.connection) {
			if (this.queue.length >= this.MAX_QUEUE) {
				this.emit('overflow', { dropped: 1, reason: 'no-connection' });
				return;
			}
			this.queue.push(outgoingMessage);
			return;
		}

		if (this.connection.readyState === WebSocket.OPEN) {
			try {
				this.connection.send(JSON.stringify(outgoingMessage));
			} catch (err) {
				if (this.queue.length >= this.MAX_QUEUE) {
					this.emit('overflow', { dropped: 1, reason: 'send-error' });
				} else {
					this.queue.push(outgoingMessage);
				}
			}
		} else {
			if (this.queue.length >= this.MAX_QUEUE) {
				this.emit('overflow', { dropped: 1, reason: 'not-open' });
			} else {
				this.queue.push(outgoingMessage);
			}
		}
	}

	public send(message: ILiveMessageDTO) {
		// eslint-disable-next-line no-console
		Logger.log('Sending websocket message:', message);
		this._send(message);
	}

	// Optional: call to stop auto-reconnect + close
	public shutdown(code = 1000, reason = 'client closing') {
		this.manualClose = true;
		this.stopHeartbeat();
		this.stopIdleWatch();
		try {
			this.connection?.close?.(code, reason);
		} catch {}
	}
}
